import { useReminders } from './useReminders';
import { useHasPermission } from '@/hooks/usePermissions';
import type { Reminder } from '@/types/reminder';

function latestOfType(items: Reminder[], type: Reminder['type']): Reminder | null {
  return (
    items
      .filter((reminder) => reminder.type === type)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null
  );
}

/**
 * The confirmation and reminder sequences for one visit.
 *
 * `GET /reminders` requires `reminders:view`, which limited roles (technicians,
 * dispatchers, sales) do not hold. Visit screens are open to those roles, so the
 * query only runs for users who can read reminders; everyone else gets no
 * reminder data (`canView: false`) instead of a 403 on every poll.
 */
export function useAppointmentReminders(appointmentId: string | null | undefined) {
  const canView = useHasPermission('reminders:view');
  const query = useReminders(
    { targetType: 'APPOINTMENT', targetId: appointmentId ?? undefined },
    { refetchInterval: 5_000, enabled: canView && Boolean(appointmentId) },
  );
  const items = query.data?.data ?? [];

  return {
    confirmation: latestOfType(items, 'JOB_CONFIRMATION'),
    reminder: latestOfType(items, 'JOB_REMINDER'),
    isLoading: query.isLoading,
    isError: query.isError,
    canView,
  };
}
